<template>
  <div class="image-uploader">
    <!-- 라벨 -->
    <div v-if="label" class="image-uploader__label">{{ label }}</div>

    <!-- 업로드 영역: 이미지가 없을 때 -->
    <div v-if="!modelValue" class="image-uploader__area">
      <a-upload
        :show-upload-list="false"
        :before-upload="handleBeforeUpload"
        accept="image/jpeg,image/png,image/webp"
        drag
      >
        <div class="image-uploader__placeholder">
          <!-- 업로드 중 프로그레스 -->
          <div v-if="uploading" class="image-uploader__progress">
            <a-progress :percent="uploadProgress" :show-info="false" />
            <span class="image-uploader__progress-text">업로드 중...</span>
          </div>
          <!-- 기본 안내 영역 -->
          <div v-else>
            <inbox-outlined class="image-uploader__icon" />
            <p class="image-uploader__hint">
              클릭하거나 파일을 드래그하세요
            </p>
            <p class="image-uploader__hint-sub">
              JPG, PNG, WebP / 최대 {{ maxSizeMB }}MB
            </p>
          </div>
        </div>
      </a-upload>
    </div>

    <!-- 미리보기: 이미지가 있을 때 -->
    <div v-else class="image-uploader__preview">
      <img :src="modelValue" alt="업로드된 이미지" class="image-uploader__img" />
      <!-- 이미지 삭제 버튼 -->
      <a-button
        type="text"
        danger
        class="image-uploader__delete"
        @click="handleDelete"
      >
        <delete-outlined />
        삭제
      </a-button>
    </div>
  </div>
</template>

<script>
import { message } from 'ant-design-vue';
import { InboxOutlined, DeleteOutlined } from '@ant-design/icons-vue';
import { HomepageApi } from '../../api';

export default {
  name: 'ImageUploader',

  components: {
    InboxOutlined,
    DeleteOutlined,
  },

  props: {
    // 현재 이미지 URL (v-model)
    modelValue: {
      type: String,
      default: '',
    },
    // 업로드 영역 라벨
    label: {
      type: String,
      default: '',
    },
    // 최대 파일 크기 (MB 단위)
    maxSizeMB: {
      type: Number,
      default: 5,
    },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      uploading: false,
      uploadProgress: 0,
    };
  },

  methods: {
    // 업로드 전 파일 유효성 검사 및 업로드 실행
    async handleBeforeUpload(file) {
      // 파일 형식 검사 (jpg/png/webp)
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        message.error('JPG, PNG, WebP 형식의 이미지만 업로드할 수 있습니다.');
        return false;
      }

      // 파일 크기 검사
      const maxBytes = this.maxSizeMB * 1024 * 1024;
      if (file.size > maxBytes) {
        message.error(`파일 크기는 ${this.maxSizeMB}MB 이하여야 합니다.`);
        return false;
      }

      // 업로드 실행
      await this.uploadFile(file);

      // ant-design-vue의 자체 업로드 동작 차단
      return false;
    },

    // 서버에 이미지 파일 업로드
    async uploadFile(file) {
      this.uploading = true;
      this.uploadProgress = 0;

      try {
        // 프로그레스 시뮬레이션 (실제 진행률 이벤트가 없는 경우 대비)
        const progressInterval = setInterval(() => {
          if (this.uploadProgress < 80) {
            this.uploadProgress += 10;
          }
        }, 200);

        const response = await HomepageApi.uploadImage(file);
        clearInterval(progressInterval);
        this.uploadProgress = 100;

        // 응답에서 이미지 URL 추출
        const imageUrl = response?.data?.url || response?.data?.imageUrl || '';
        if (!imageUrl) {
          throw new Error('이미지 URL을 받지 못했습니다.');
        }

        this.$emit('update:modelValue', imageUrl);
      } catch (error) {
        message.error(error.message || '이미지 업로드 중 오류가 발생했습니다.');
      } finally {
        this.uploading = false;
        this.uploadProgress = 0;
      }
    },

    // 이미지 삭제 처리
    handleDelete() {
      this.$emit('update:modelValue', '');
    },
  },
};
</script>

<style scoped>
.image-uploader__label {
  margin-bottom: 6px;
  font-size: 13px;
  color: #555;
  font-weight: 500;
}

.image-uploader__area :deep(.ant-upload.ant-upload-drag) {
  border-radius: 6px;
}

.image-uploader__placeholder {
  padding: 16px;
  text-align: center;
}

.image-uploader__icon {
  font-size: 28px;
  color: #aaa;
  margin-bottom: 8px;
}

.image-uploader__hint {
  margin: 0 0 4px;
  font-size: 13px;
  color: #666;
}

.image-uploader__hint-sub {
  margin: 0;
  font-size: 11px;
  color: #aaa;
}

.image-uploader__progress {
  padding: 8px 0;
}

.image-uploader__progress-text {
  font-size: 12px;
  color: #888;
}

.image-uploader__preview {
  position: relative;
  display: inline-block;
}

.image-uploader__img {
  max-width: 100%;
  max-height: 200px;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
  display: block;
}

.image-uploader__delete {
  margin-top: 6px;
  font-size: 12px;
}
</style>
